import { Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconCoin, IconInfoCircle } from "@tabler/icons-react";
import {
  estimateFeePaise,
  estimateSharePaise,
  formatPaiseRange,
} from "../lib/format";
import { axioTokens } from "../theme";

type EarningsJob = {
  fee_basis: string;
  fee_flat_paise: number | null;
  fee_pct_ctc: number | null;
  ctc_min_paise: number | null;
  ctc_max_paise: number | null;
  sourcing_fee_pct?: number | null;
  recruiter_fee_pct?: number | null;
  platform_fee_pct?: number | null;
};

function shareLabel(share: { min: number; max: number } | null): string {
  if (!share) return "—";
  return formatPaiseRange(share.min, share.max);
}

const PLATFORM_FEE_LABEL = "Platform";
const PLATFORM_FEE_DETAIL_LABEL = "Platform fee";
const PLATFORM_FEE_TOOLTIP = "What AxioHire keeps of the client fee. Place and source go to you.";

interface Props {
  job: EarningsJob;
  /** Compact strip for browse cards; full panel for detail pages. */
  variant?: "compact" | "full";
}

/** Earnings breakdown for recruiter job cards / detail. */
export function RecruiterJobEarnings({ job, variant = "full" }: Props) {
  const fee = estimateFeePaise(job);
  const recruiterShare = estimateSharePaise(fee, job.recruiter_fee_pct);
  const sourcingShare = estimateSharePaise(fee, job.sourcing_fee_pct);
  const platformShare = estimateSharePaise(fee, job.platform_fee_pct);
  const combined =
    recruiterShare || sourcingShare
      ? {
          min: (recruiterShare?.min ?? 0) + (sourcingShare?.min ?? 0),
          max: (recruiterShare?.max ?? 0) + (sourcingShare?.max ?? 0),
        }
      : null;
  const hasRange =
    (recruiterShare && recruiterShare.min !== recruiterShare.max) ||
    (sourcingShare && sourcingShare.min !== sourcingShare.max);

  if (variant === "compact") {
    return (
      <Stack
        gap="var(--axio-unit-8)"
        p="sm"
        style={{
          borderRadius: axioTokens.radius.md,
          background: axioTokens.earningsCompact,
          border: `${axioTokens.unit[1]}px solid ${axioTokens.border}`,
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="var(--axio-unit-6)" wrap="nowrap">
            <IconCoin size="var(--axio-unit-15)" color={axioTokens.money} stroke="var(--axio-stroke-width-regular)" />
            <Text size="xs" fw="var(--axio-font-weight-semibold)" c="dimmed" tt="uppercase" style={{ letterSpacing: "var(--axio-letter-spacing-label)" }}>
              You can earn
            </Text>
            <Tooltip
              label="If you source and place the hire. Final amount depends on offered CTC."
              withArrow
              multiline
              w="var(--axio-unit-220)"
            >
              <IconInfoCircle size="var(--axio-unit-13)" color={axioTokens.muted} style={{ flexShrink: "var(--axio-number-zero)" }} />
            </Tooltip>
          </Group>
          <Text fw="var(--axio-font-weight-extra-bold)" size="lg" c="money.8" style={{ fontFamily: axioTokens.fontHead, letterSpacing: "var(--axio-letter-spacing-tight)", lineHeight: "var(--axio-line-height-solid)" }}>
            ≈ {shareLabel(combined)}
          </Text>
        </Group>
        <Group gap="md" wrap="nowrap">
          <Text size="xs" c="dimmed">
            Place <Text span fw="var(--axio-font-weight-semibold)" c="violet.8" size="xs">≈ {shareLabel(recruiterShare)}</Text>
          </Text>
          <Text size="xs" c="dimmed">
            Source <Text span fw="var(--axio-font-weight-semibold)" c="teal.8" size="xs">≈ {shareLabel(sourcingShare)}</Text>
          </Text>
          {platformShare ? (
            <Tooltip label={PLATFORM_FEE_TOOLTIP} withArrow multiline w="var(--axio-unit-220)">
              <Text size="xs" c="dimmed">
                {PLATFORM_FEE_LABEL}{" "}
                <Text span fw="var(--axio-font-weight-semibold)" size="xs">
                  ≈ {shareLabel(platformShare)}
                </Text>
              </Text>
            </Tooltip>
          ) : null}
        </Group>
      </Stack>
    );
  }

  return (
    <Stack
      gap="var(--axio-unit-10)"
      p="md"
      style={{
        borderRadius: axioTokens.radius.lg,
        background: axioTokens.earningsFull,
        border: "var(--axio-unit-1) solid var(--mantine-color-violet-2)",
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="var(--axio-unit-6)">
          <IconCoin size="var(--axio-unit-16)" color="var(--mantine-color-violet-7)" />
          <Text size="xs" tt="uppercase" fw="var(--axio-font-weight-bold)" c="violet.8" style={{ letterSpacing: "var(--axio-letter-spacing-label)" }}>
            Approx earnings
          </Text>
        </Group>
        <Text fw="var(--axio-font-weight-extra-bold)" size="xl" c="violet.9" ta="right" style={{ letterSpacing: "var(--axio-letter-spacing-tighter)", lineHeight: "var(--axio-line-height-tight)" }}>
          ≈ {shareLabel(combined)}
        </Text>
      </Group>
      <Text size="xs" c="dimmed" mt="calc(var(--axio-unit-4) * -1)">
        If you source + place the hire
      </Text>

      <Stack gap="var(--axio-unit-6)">
        <Group justify="space-between" align="baseline" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Recruiter fee
          </Text>
          <Text fw="var(--axio-font-weight-bold)" size="sm" c="violet.9">
            ≈ {shareLabel(recruiterShare)}
          </Text>
        </Group>
        <Group justify="space-between" align="baseline" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Sourcing fee
          </Text>
          <Text fw="var(--axio-font-weight-bold)" size="sm" c="teal.8">
            ≈ {shareLabel(sourcingShare)}
          </Text>
        </Group>
        {platformShare ? (
          <Tooltip label={PLATFORM_FEE_TOOLTIP} withArrow multiline w="var(--axio-unit-220)">
            <Group justify="space-between" align="baseline" wrap="nowrap">
              <Text size="sm" c="dimmed">
                {PLATFORM_FEE_DETAIL_LABEL}
              </Text>
              <Text fw="var(--axio-font-weight-bold)" size="sm">
                ≈ {shareLabel(platformShare)}
              </Text>
            </Group>
          </Tooltip>
        ) : null}
      </Stack>

      <Text size="xs" c="dimmed" style={{ lineHeight: "var(--axio-line-height-body)" }}>
        Sourcing fee is yours if you bring the candidate. Close with your own lead and earn both.
        {hasRange ? " Approx. based on CTC band — final amount depends on offered CTC." : ""}
      </Text>
    </Stack>
  );
}
